import React, { useCallback, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { useGoogleMaps } from '../../../hooks/useGoogleMaps.ts';
import styles from './PlacesAutocomplete.module.scss';

export interface GeocoderAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface PlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (place: {
    address: string;
    lat: number;
    lng: number;
    addressComponents?: GeocoderAddressComponent[];
  }) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
  hideLabel?: boolean;
}

// Componente interno que maneja el autocomplete
const PlacesAutocompleteInner: React.FC<PlacesAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Ingresa la ubicación del proyecto",
  className = "",
  error,
  disabled = false,
  hideLabel = false
}) => {
  const {
    ready,
    value: inputValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'ar' }, // Solo Argentina
      types: ['establishment', 'geocode'],
    },
    debounce: 300,
  });

  // Sincronizar valor externo con el hook
  useEffect(() => {
    if (value !== inputValue) {
      setValue(value, false);
    }
  }, [value, inputValue, setValue]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onChange(newValue);
  }, [setValue, onChange]);

  const handleSelect = useCallback(async (suggestion: any) => {
    const { place_id, description } = suggestion;
    
    try {
      const results = await getGeocode({ placeId: place_id });
      const result = results[0];
      const { lat, lng } = await getLatLng(result);

      const addressComponents = result?.address_components?.map((ac: GeocoderAddressComponent) => ({
        long_name: ac.long_name,
        short_name: ac.short_name,
        types: ac.types || [],
      }));

      setValue(description, false);
      onChange(description);
      clearSuggestions();

      if (onPlaceSelect) {
        onPlaceSelect({
          address: description,
          lat,
          lng,
          addressComponents,
        });
      }
    } catch (error) {
      console.error('Error al obtener coordenadas:', error);
      // Fallback: solo usar la descripción
      setValue(description, false);
      onChange(description);
      clearSuggestions();
    }
  }, [setValue, onChange, clearSuggestions, onPlaceSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      clearSuggestions();
    }
  }, [clearSuggestions]);

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.inputWrapper}>
        {!hideLabel && (
          <label className={styles.label}>
            <MapPin size={18} />
            Ubicación *
          </label>
        )}
        <input
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || !ready}
          placeholder={placeholder}
          className={`${styles.input} ${error ? styles.inputError : ''}`}
          autoComplete="off"
        />
        {error && (
          <span className={styles.errorText}>{error}</span>
        )}
      </div>

      {/* Sugerencias */}
      {status === 'OK' && data.length > 0 && (
        <div className={styles.suggestions}>
          {data.map((suggestion) => {
            const {
              place_id,
              structured_formatting: { main_text, secondary_text },
            } = suggestion;

            return (
              <div
                key={place_id}
                className={styles.suggestionItem}
                onClick={() => handleSelect(suggestion)}
                onMouseDown={(e) => e.preventDefault()} // Prevenir blur del input
              >
                <div className={styles.suggestionContent}>
                  <div className={styles.mainText}>{main_text}</div>
                  <div className={styles.secondaryText}>{secondary_text}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Loading state */}
      {status === 'LOADING' && (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <span>Buscando ubicaciones...</span>
        </div>
      )}

      {/* No results */}
      {status === 'ZERO_RESULTS' && inputValue && (
        <div className={styles.noResults}>
          <span>No se encontraron ubicaciones para "{inputValue}"</span>
        </div>
      )}
    </div>
  );
};

// Componente principal que maneja la carga de Google Maps
const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = (props) => {
  const { isLoaded, loadError } = useGoogleMaps();

  // Mostrar error de carga de Google Maps
  if (loadError) {
    return (
      <div className={`${styles.container} ${props.className || ''}`}>
        <div className={styles.inputWrapper}>
          {!props.hideLabel && (
            <label className={styles.label}>
              <MapPin size={18} />
              Ubicación *
            </label>
          )}
          <input
            value={props.value}
            onChange={(e) => props.onChange(e.target.value)}
            placeholder="Ingresa la ubicación manualmente"
            className={`${styles.input} ${props.error ? styles.inputError : ''}`}
            autoComplete="off"
          />
          <span className={styles.errorText}>
            Error al cargar Google Maps. Ingresa la ubicación manualmente.
          </span>
        </div>
      </div>
    );
  }

  // Mostrar loading state
  if (!isLoaded) {
    return (
      <div className={`${styles.container} ${props.className || ''}`}>
        <div className={styles.inputWrapper}>
          {!props.hideLabel && (
            <label className={styles.label}>
              <MapPin size={18} />
              Ubicación *
            </label>
          )}
          <input
            value={props.value}
            onChange={(e) => props.onChange(e.target.value)}
            placeholder="Cargando Google Maps..."
            className={`${styles.input} ${props.error ? styles.inputError : ''}`}
            autoComplete="off"
            disabled
          />
          <span className={styles.loadingText}>Cargando Google Maps...</span>
        </div>
      </div>
    );
  }

  // Renderizar el componente con autocomplete cuando Google Maps esté listo
  return <PlacesAutocompleteInner {...props} />;
};

export default PlacesAutocomplete;
